import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth, login } from "@/context/AuthContext";
import {
  homesSavedIds, homesSaveListing, homesUnsaveListing, homesAlertsCount,
} from "@/lib/api";

const SavedContext = createContext(null);
export const useSaved = () => useContext(SavedContext);

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const [savedIds, setSavedIds] = useState(new Set());
  const [alerts, setAlerts] = useState({ new_matches: 0, saved_listings: 0 });

  const reload = useCallback(async () => {
    if (!user) { setSavedIds(new Set()); setAlerts({ new_matches: 0, saved_listings: 0 }); return; }
    try {
      const [ids, a] = await Promise.all([homesSavedIds(), homesAlertsCount()]);
      setSavedIds(new Set(ids.listing_ids));
      setAlerts(a);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const isSaved = useCallback((id) => savedIds.has(id), [savedIds]);

  const toggle = useCallback(async (id) => {
    if (!user) {
      toast("Sign in to save homes", { description: "Create a free account to save listings and searches." });
      login(window.location.pathname);
      return;
    }
    const has = savedIds.has(id);
    const next = new Set(savedIds);
    has ? next.delete(id) : next.add(id);
    setSavedIds(next);
    setAlerts((a) => ({ ...a, saved_listings: a.saved_listings + (has ? -1 : 1) }));
    try {
      has ? await homesUnsaveListing(id) : await homesSaveListing(id);
      toast(has ? "Removed from saved" : "Saved to your homes");
    } catch {
      // revert with a NEW Set so React re-renders, and roll back the counter
      setSavedIds((prev) => {
        const revert = new Set(prev);
        has ? revert.add(id) : revert.delete(id);
        return revert;
      });
      setAlerts((a) => ({ ...a, saved_listings: a.saved_listings + (has ? 1 : -1) }));
      toast.error("Could not update saved homes");
    }
  }, [user, savedIds]);

  return (
    <SavedContext.Provider value={{ savedIds, isSaved, toggle, alerts, reload, user }}>
      {children}
    </SavedContext.Provider>
  );
}
